import Vue from "vue";
import * as Sentry from "@sentry/vue";
import { filterValues } from "../sentry";

import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  concat,
  from,
  InMemoryCache
} from "@apollo/client/core";
import { onError } from "@apollo/client/link/error";
import { createUploadLink } from "apollo-upload-client";
import CacheTypePolicy from "./cache_type_policy.js";
import auth from "@/utils/auth.js";

import Cookies from "js-cookie";

import VueApollo from "vue-apollo";
Vue.use(VueApollo);

import { loadDevMessages } from "@apollo/client/dev";
if (process.env.NODE_ENV === "development") {
  // Adds messages only in a dev environment
  loadDevMessages();
}

const IGNORE_OPERATIONS = ["frontendVersionCheck", "personBasketInterval"];

const errorLink = onError(({ operation, graphQLErrors, networkError }) => {
  const consoleLoggable =
    Cookies.get("enable_tehnical") === true ||
    process.env.NODE_ENV != "production";

  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, path }) => {
      if (consoleLoggable) {
        console.error(
          `[GraphQL error]: Message: ${message}; Operation: ${operation.operationName}; Path: ${path}`
        );
      }
      Sentry.withScope(scope => {
        scope.setContext("Error details", {
          path: path,
          operationName: operation.operationName,
          variables: filterValues(operation.variables)
        });
        scope.setTag("network_error", "graphql_error");
        scope.setLevel("error");
        Sentry.captureMessage(message);
      });
    });
  }
  if (networkError) {
    if (consoleLoggable) {
      console.warn(
        `[Network error]: ${networkError}; Operation: ${operation.operationName}`
      );
    }

    if (IGNORE_OPERATIONS.includes(operation.operationName)) return; // skip reporting these

    Sentry.withScope(scope => {
      scope.setContext("Error details", {
        operationName: operation.operationName,
        statusCode: networkError.statusCode ?? "unknown",
        name: networkError.name,
        message: networkError
      });

      scope.setLevel("warning");

      if (networkError.statusCode) {
        scope.setTag("network_error", networkError.statusCode);
        Sentry.captureMessage("Encountered network error");
      } else {
        scope.setTag("network_error", "timeout");
        Sentry.captureMessage(
          `Encountered timeout, user left or aborted request? (missing network statusCode)`
        );
      }
    });
  }
});

const clientLink = createUploadLink({
  uri: process.env.VUE_APP_API_URL + "/graphql"
});

const authMiddleware = new ApolloLink((operation, forward) => {
  const token = auth.getToken();
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      // "Access-Control-Allow-Origin": "*",
      authorization: token ? `Bearer ${token}` : "",
      "fipa-cid": auth.getClientId(),
      "fipa-cver": process.env.VUE_APP_RELEASE_VERSION,
      "fipa-locale":
        localStorage.getItem("lang") || process.env.VUE_APP_LANGUAGE
    }
  }));

  Sentry.addBreadcrumb({
    type: "query",
    category: "query",
    message: operation.operationName,
    data: {
      ...filterValues(operation.variables),
      operationName: operation.operationName
    },
    level: "info"
  });

  return forward(operation);
});

const cache = new InMemoryCache(CacheTypePolicy);
export const apolloClient = new ApolloClient({
  link: from([errorLink, concat(authMiddleware, clientLink)]),
  cache,
  connectToDevTools: process.env.NODE_ENV === "development",
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
      error: "none"
    },
    query: {
      fetchPolicy: "network-only",
      errorPolicy: "none"
    },
    mutate: {
      errorPolicy: "none"
    }
  }
});

import historyPaginationFields from "./history_pagination_fields.js";
const oldDataHistory = new ApolloClient({
  link: new HttpLink({
    uri: "https://prod-feds-history-api-fi.herokuapp.com/v1/graphql"
  }),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: historyPaginationFields
      }
    }
  })
});

export const apolloProvider = new VueApollo({
  clients: {
    primary: apolloClient,
    historyData: oldDataHistory
  },
  defaultClient: apolloClient,
  errorHandler() {
    /* Do nothing */
  }
});
